/**
 * When the connectivity banner shows and hides - display only. The connectivity signal underneath
 * (effectiveConnectivity -> onlineManager) is NOT delayed; data fetching still reacts at once. This only stops the bar
 * blinking in and out on a festival network that flaps every few seconds (operator, 2026-09-25: "too volatile").
 *
 * Hysteresis:
 *   - "Offline" shows only after the signal has said unreachable continuously for OFFLINE_SHOW_AFTER_MS.
 *   - "Slow connection" shows only after it has been bad (slow or offline) continuously for LOW_SERVICE_SHOW_AFTER_MS.
 *   - Once shown, it hides only after the signal has been fine continuously for HIDE_AFTER_MS, and steps down from
 *     Offline to Slow only after slow has held that long. A blip back to bad restarts that clock and the bar stays.
 */
export const OFFLINE_SHOW_AFTER_MS = 5_000;
export const LOW_SERVICE_SHOW_AFTER_MS = 10_000;
export const HIDE_AFTER_MS = 3_000;

export const BANNER_UNREACHABLE = 'unreachable';
export const BANNER_DEGRADED = 'degraded';

/** Fold a new raw reading into the tracker: { raw, rawSince, badSince }. Returns the same object if nothing changed. */
export function trackConnectivityVariant(track, raw, now) {
  const next = raw ?? null;
  if (track && track.raw === next) return track;
  const prevBad = Boolean(track?.raw);
  return {
    raw: next,
    rawSince: now,
    badSince: next ? (prevBad ? track.badSince : now) : null,
  };
}

/**
 * What the banner should show now, and how long until the answer could change on its own (Infinity = only on a new
 * reading).
 */
export function decideBannerVariant(visible, track, now) {
  const raw = track?.raw ?? null;
  const held = now - (track?.rawSince ?? now);
  const bad = track?.badSince != null ? now - track.badSince : 0;

  if (raw === null) {
    if (visible === null) return { visible: null, recheckMs: Infinity };
    if (held >= HIDE_AFTER_MS) return { visible: null, recheckMs: Infinity };
    return { visible, recheckMs: HIDE_AFTER_MS - held };
  }

  if (raw === BANNER_UNREACHABLE) {
    if (visible === BANNER_UNREACHABLE || held >= OFFLINE_SHOW_AFTER_MS) {
      return { visible: BANNER_UNREACHABLE, recheckMs: Infinity };
    }
    const untilOffline = OFFLINE_SHOW_AFTER_MS - held;
    if (visible === BANNER_DEGRADED || bad >= LOW_SERVICE_SHOW_AFTER_MS) {
      return { visible: BANNER_DEGRADED, recheckMs: untilOffline };
    }
    return { visible: null, recheckMs: Math.min(untilOffline, LOW_SERVICE_SHOW_AFTER_MS - bad) };
  }

  // raw === degraded
  if (visible === BANNER_DEGRADED) return { visible, recheckMs: Infinity };
  if (visible === BANNER_UNREACHABLE) {
    if (held >= HIDE_AFTER_MS) return { visible: BANNER_DEGRADED, recheckMs: Infinity };
    return { visible, recheckMs: HIDE_AFTER_MS - held };
  }
  if (bad >= LOW_SERVICE_SHOW_AFTER_MS) return { visible: BANNER_DEGRADED, recheckMs: Infinity };
  return { visible: null, recheckMs: LOW_SERVICE_SHOW_AFTER_MS - bad };
}
